import React from "react";
import { Box, Plus } from "lucide-react";
import { bomSummary, BOM_STATE_CLS, BOM_STATE_TITLE } from "./bomUtils";

/* Assembly glyph — one parent box over two children (a tiny BOM tree), drawn
 * in lucide's stroke style so it sits beside lucide's Box (single part). */
export function AssemblyIcon({ className = "w-3.5 h-3.5", strokeWidth = 1.9 }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth={strokeWidth}
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="8.5" y="2.5" width="7" height="6" rx="1.3" />
      <rect x="2.5" y="15.5" width="7" height="6" rx="1.3" />
      <rect x="14.5" y="15.5" width="7" height="6" rx="1.3" />
      <path d="M12 8.5v3.5M6 15.5V12h12v3.5" />
    </svg>
  );
}

/* Single part | Assembly — the carved-track segmented control (same family as
 * the density toggle). Locked states stay visible, greyed, with the reason in
 * the tooltip — never a dead end. */
export function BuildTypeToggle({ item, canEdit, lockedReason, onChange, isCompact }) {
  const val = item.build_type === "assembly" ? "assembly" : "single";
  const opts = [
    { key: "single", label: "Single part", Icon: Box },
    { key: "assembly", label: "Assembly", Icon: AssemblyIcon },
  ];
  return (
    <div
      role="radiogroup"
      aria-label="Single part or assembly"
      className={`inline-flex items-center p-[2px] rounded-full border border-[hsl(var(--border))] bg-[hsl(var(--muted))]/50 ${!canEdit ? "opacity-70" : ""}`}
      data-testid={`build-type-${item.id}`}
    >
      {opts.map(({ key, label, Icon }) => {
        const on = val === key;
        const disabled = !canEdit || (!on && !!lockedReason && key === "single");
        return (
          <button
            key={key}
            type="button"
            role="radio"
            aria-checked={on}
            disabled={disabled}
            title={disabled && !on ? (lockedReason || "View only") : on ? `${label} (current)` : `Make this line ${key === "single" ? "a single part" : "an assembly"}`}
            onClick={() => !on && onChange?.(item, key)}
            className={`inline-flex items-center gap-1.5 rounded-full font-semibold transition-colors ${isCompact ? "h-5 px-2 text-[10px]" : "h-6 px-2.5 text-[11px]"} ${
              on
                ? "bg-[hsl(var(--card))] text-[hsl(var(--foreground))] shadow-[0_1px_2px_rgba(0,0,0,0.12),0_0_0_1px_hsl(var(--border))]"
                : "text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] disabled:hover:text-[hsl(var(--muted-foreground))] disabled:cursor-not-allowed"
            }`}
            data-testid={`build-type-${key}-${item.id}`}
          >
            <Icon className={isCompact ? "w-3 h-3" : "w-3.5 h-3.5"} />
            {label}
          </button>
        );
      })}
    </div>
  );
}

/* The child-parts button. Empty list → the QD-pending treatment (breathing
 * amber dashed border + slowly turning dashed ring) because the line can't be
 * accepted until parts are added. Filled → a SOLID status-tinted pill showing
 * received / total, coloured by the worst news first. */
export function ChildPartsPill({ item, onOpen, isCompact, canAdd = true }) {
  const s = bomSummary(item.bom);
  const h = isCompact ? "h-6 text-[10px]" : "h-7 text-[11px]";
  if (!s.n) {
    return (
      <button
        type="button"
        onClick={() => onOpen?.(item)}
        disabled={!canAdd}
        className={`inline-flex items-center gap-1.5 ${h} px-2.5 rounded-full border border-dashed border-amber-500/60 bg-amber-500/5 text-amber-700 dark:text-amber-400 font-semibold whitespace-nowrap transition-colors ${canAdd ? "hover:bg-amber-500/15 qd-pulse" : "opacity-60 cursor-not-allowed"}`}
        title={canAdd ? "This assembly has no child parts yet — add them so Material can start buying" : "No child parts yet — PO Received adds them"}
        data-testid={`child-parts-empty-${item.id}`}
      >
        <span className="w-3.5 h-3.5 rounded-full border border-dashed border-amber-500/80 shrink-0 inline-flex items-center justify-center qd-dash-spin">
          <Plus className="w-2 h-2" strokeWidth={3} />
        </span>
        Add child parts
      </button>
    );
  }
  return (
    <button
      type="button"
      onClick={() => onOpen?.(item)}
      className={`inline-flex items-center gap-1.5 ${h} px-2.5 rounded-full border font-semibold whitespace-nowrap transition-colors ${BOM_STATE_CLS[s.state]}`}
      title={`${BOM_STATE_TITLE[s.state]} — open the Child Part Tracker`}
      data-testid={`child-parts-pill-${item.id}`}
    >
      <AssemblyIcon className={isCompact ? "w-3 h-3" : "w-3.5 h-3.5"} />
      Child parts
      <span className="font-mono-data font-extrabold tabular-nums">{s.received}/{s.n}</span>
    </button>
  );
}

/* Sticky orange flag on an assembly that left Procurement with parts still
 * outstanding. Clears itself the moment the last part is received. */
export function PartialProcuredTag({ item, onOpen }) {
  const s = bomSummary(item.bom);
  if (item.build_type !== "assembly" || !s.n || !s.outstanding) return null;
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onOpen?.(item); }}
      className="stage-badge shrink-0 inline-flex items-center gap-1 text-[8.5px] font-bold uppercase tracking-wider px-1.5 py-[1px] rounded border border-orange-500/40 bg-orange-500/10 text-orange-700 dark:text-orange-400 hover:bg-orange-500/20 transition-colors"
      title={`${s.outstanding} child part${s.outstanding === 1 ? "" : "s"} still to come${s.late ? ` (${s.late} late)` : ""} — open the Child Part Tracker`}
      data-testid={`partial-procured-${item.id}`}
    >
      <AssemblyIcon className="w-2.5 h-2.5" strokeWidth={2.4} />
      Partially procured
      <span className="font-mono-data tracking-normal">{s.received}/{s.n}</span>
    </button>
  );
}
